import { Flex, Skeleton } from "antd";
import { sanitizeHtml } from "../../utils/helpers/sanitizeHtml";
import { RequestResults } from "../../redux/common/RequestStatus";
import { useState } from "react";
import { ActivityIcon, EditIcon, InnerFlex, StyledTitle, Timestamp } from "./style";
import { DefaultUserName, LostItemActivityLogType, LostItemLogsData } from "../../types/ActivityLog";
import { ReactComponent as EmailIcon } from "../../assets/images/email_activity.svg";
import { ReactComponent as StickyIcon } from "../../assets/images/sticky_activity.svg";
import { ReactComponent as NoteIcon } from "../../assets/images/note_activity.svg";
import { ReactComponent as ClaimIcon } from "../../assets/images/claim_activity.svg";
import { ReactComponent as EnvelopeOpenIcon } from "../../assets/images/envelopeOpen.svg";
import { ReactComponent as AddIcon } from "../../assets/images/add_activity.svg";
import { ReactComponent as CycleIcon } from "../../assets/images/cycle.svg";
import { ReactComponent as LinkIcon } from "../../assets/images/link_activity.svg";
import { ReactComponent as FoundIcon } from "../../assets/images/found_activity.svg";
import { ReactComponent as ToggleIcon } from "../../assets/images/toggle_activity.svg";
import { ReactComponent as HideIcon } from "../../assets/images/hide_activity.svg";
import { convertUtcToLocalAndRelativeTime } from "../../utils/helpers/convertUtcToLocal";
import { CalendarOutlined } from "@ant-design/icons"
import { SupportEmail } from "../../utils/constants";
import StyledModal from "../../components/CustomModal";
import { FooterButtons } from "../styles";
import { AddButton } from "../../pages/Category/styles";
import api from "../../utils/constants/api";
import { apiEndpoints } from "../../utils/constants/apiEndpoints";

interface ActivityLogsProps {
    handleEditNoteModalOpen?: (type: "add" | "update", item: LostItemLogsData) => void;
    logs: LostItemLogsData[];
    status: RequestResults;
    /** Customer's "Before We Begin" consent timestamp. Pulled from the lost row's
     *  authorizationsAcceptedAt column when present; falls back to claim createdAt
     *  for legacy claims that pre-date the column. Null hides the entry entirely. */
    authorizationsTimestamp?: string | null;
    /** When true, the timestamp is from authorizationsAcceptedAt (real consent
     *  capture). When false, it's the createdAt fallback for legacy claims — we
     *  render an "(approximated)" subtle hint so the admin doesn't mistake the
     *  fallback for explicit consent capture. */
    authorizationsHasExplicitRecord?: boolean;
}

const ActivityLogs: React.FC<ActivityLogsProps> = ({
    handleEditNoteModalOpen,
    logs,
    status,
    authorizationsTimestamp,
    authorizationsHasExplicitRecord,
}) => {
    const [selectedEmailLogs, setSelectedEmailLogs] = useState<LostItemLogsData | null>(null);
    const [emailBodyLoading, setEmailBodyLoading] = useState(false);

    // The list endpoint strips `emailLog.body` to keep response sizes small (1 claim with
    // 20 emails was returning ~1MB). Fetch the body on demand only when the admin actually
    // opens an email row. Keeps the rest of the selectedEmailLogs object (subject/from/to/…)
    // intact so the modal renders headers instantly while the body loads.
    const openEmailLog = async (item: LostItemLogsData) => {
        setSelectedEmailLogs(item);
        const emailLogId = item?.emailLog?.id;
        if (!emailLogId || (item?.emailLog as any)?.body) return;
        try {
            setEmailBodyLoading(true);
            const response: any = await api.get(`${apiEndpoints.activityLog.getEmailBody}/${emailLogId}`);
            const body: string | null = response?.data?.body ?? null;
            setSelectedEmailLogs(prev => prev && prev.emailLog?.id === emailLogId
                ? { ...prev, emailLog: { ...prev.emailLog, body: body ?? "" } as any }
                : prev);
        } catch (e) {
            // Leave body blank; the modal already shows a fallback when body is empty.
        } finally {
            setEmailBodyLoading(false);
        }
    };

    const handleCloseEmailLogsPopup = () => {
        setSelectedEmailLogs(null)
    }

    const renderClaimStatusChangeActivity = (item: LostItemLogsData, showNote?: boolean) => {
        const userName = item?.userName || item?.adminName || "";

        if (userName && item?.description) {
            return `${userName} ${item?.description} ${showNote ? item?.note ?? "" : ""}`
        } else if (item?.emailLog?.id) {
            return `${DefaultUserName} sent ${item?.emailLog?.subject} email`;
        } else if (userName && !item?.description) {
            return `${DefaultUserName} moved from Open to In Process or if it's a manual move before System trigger: ${userName} moved from Open to In Process`
        } else if (item?.description) {
            return `${item?.description} ${item?.note ?? ""}`;
        } else if (!item?.description && !item?.emailLog?.id && !userName) {
            return `${DefaultUserName} sent escalation update`;
        }
        return "";
    }

    const renderActivityItem = (item: LostItemLogsData) => {
        let icon;
        let description;
        const userNameOrAdminName = item?.userName || item?.adminName || "";
        // Choose the icon and description based on item type
        switch (Number(item?.type)) {
            case LostItemActivityLogType.StickyAdded:
                icon = <StickyIcon />;
                description = renderClaimStatusChangeActivity(item, true);
                break;
            case LostItemActivityLogType.StickyUpdated:
                icon = <StickyIcon />;
                description = renderClaimStatusChangeActivity(item, true);
                break;
            case LostItemActivityLogType.StickyDone:
                icon = <StickyIcon />;
                description = userNameOrAdminName && item?.description ? `${userNameOrAdminName} ${item?.description}` : "";
                break;
            case LostItemActivityLogType.SendLostRequest:
                icon = <EnvelopeOpenIcon />;
                description = userNameOrAdminName && item?.description ? `${userNameOrAdminName} ${item?.description}` : item?.emailLog?.id ? `${DefaultUserName} sent ${item?.emailLog?.subject} email (report PDF attached)` : "";
                break;
            case LostItemActivityLogType.SendFoundRequest:
                icon = <EnvelopeOpenIcon />;
                description = userNameOrAdminName && item?.description ? `${userNameOrAdminName} ${item?.description}` : item?.emailLog?.id ? `${DefaultUserName} sent ${item?.emailLog?.subject} email (report PDF attached)` : "";
                break;
            case LostItemActivityLogType.ClaimStatusChange:
                icon = <CycleIcon />;
                description = renderClaimStatusChangeActivity(item, false);
                break;
            case LostItemActivityLogType.FoundItemStatusChanged:
                icon = <CycleIcon />;
                description = renderClaimStatusChangeActivity(item, false);
                break;
            case LostItemActivityLogType.ClaimUpdated:
            case LostItemActivityLogType.ClaimUpdateRequestReceived:
            case LostItemActivityLogType.BillingInfoUpdateRequestReceived:
            case LostItemActivityLogType.ClaimDeleteRequestReceived:
                icon = <ClaimIcon />;
                description = `${userNameOrAdminName} ${item?.description}`;
                break;
            case 7: // Backend EmailSent type (ActivityLog.EmailSent = 7) - not EmailSent enum (10)
                icon = <EnvelopeOpenIcon />;
                description = `${userNameOrAdminName || DefaultUserName} ${item?.description}`;
                break;
            case 8: // Backend NoteAdded type (ActivityLog.NoteAdded = 8) - not Note enum (11)
                icon = <NoteIcon />;
                description = `${userNameOrAdminName || DefaultUserName} ${item?.description} ${item?.note ?? ""}`;
                break;
            case LostItemActivityLogType.SystemMovedLostItem:
                icon = <CycleIcon />;
                description = `${DefaultUserName} ${item?.description}`;
                break;
            case 10: // Backend ToggleUrgent type (ActivityLog.ToggleUrgent = 10) - conflicts with EmailSent enum (10)
            case 11: // Backend ToggleEmailCommunication type (ActivityLog.ToggleEmailCommunication = 11) - conflicts with Note enum (11)
            case LostItemActivityLogType.ToggleUrgent: // Frontend enum value (14)
            case LostItemActivityLogType.ToggleEmailCommunication: // Frontend enum value (15)
                icon = <ToggleIcon />;
                description = `${userNameOrAdminName ?? DefaultUserName} ${item?.description}`;
                break;
            case LostItemActivityLogType.LostDateUpdated:
                icon = <CalendarOutlined />;
                description = `${userNameOrAdminName ?? DefaultUserName} ${item?.description}`;
                break;
            case LostItemActivityLogType.ClaimUpdateRequestApproved:
                icon = <ClaimIcon />;
                description = `${userNameOrAdminName ?? DefaultUserName} ${item?.description}`;
                break;
            case LostItemActivityLogType.ClaimUpdateRequestRejected:
                icon = <ClaimIcon />;
                description = `${userNameOrAdminName ?? DefaultUserName} ${item?.description}`;
                break;
            case LostItemActivityLogType.ClaimDeleteRequestApproved:
                icon = <ClaimIcon />;
                description = `${userNameOrAdminName ?? DefaultUserName} ${item?.description}`;
                break;
            case LostItemActivityLogType.ClaimDeleteRequestRejected:
                icon = <ClaimIcon />;
                description = `${userNameOrAdminName ?? DefaultUserName} ${item?.description}`;
                break;
            case LostItemActivityLogType.ClaimUpdateBillingInfoRequestApproved:
            case LostItemActivityLogType.ClaimUpdateBillingInfoRequestRejected:
                icon = <ClaimIcon />;
                description = `${userNameOrAdminName} ${item?.description} ${item?.note ?? ""}`;
                break;
            case LostItemActivityLogType.ClaimPaymentSuccess:
                icon = <ClaimIcon />;
                description = `${userNameOrAdminName} ${item?.description} ${item?.note ?? ""}`;
                break;
            case LostItemActivityLogType.ClaimPaymentFailed:
                icon = <ClaimIcon />;
                description = `${item?.description} ${item?.note ?? ""}`;
                break;
            case LostItemActivityLogType.ClaimPaymentRefunded:
                icon = <ClaimIcon />;
                description = `${item?.description} ${item?.note ?? ""}`;
                break;
            case LostItemActivityLogType.SendPaymentRequest:
                icon = <EnvelopeOpenIcon />;
                description = `${userNameOrAdminName ?? DefaultUserName} ${item?.description} ${item?.note ?? ""}`;
                break;
            case 29: // Backend ActivityLog.UserAccountCreationEmail = 29
                icon = <EnvelopeOpenIcon />;
                description = item?.description || `${DefaultUserName} sent user account creation email`;
                break;
            default:
                icon = <EnvelopeOpenIcon />;
                description = `${DefaultUserName} Performed action`;
                break;
        }

        return (
            <Flex align="center" gap={10}>
                <ActivityIcon>{icon}</ActivityIcon>
                <span>{description}
                    {item?.emailLog?.id && (
                        <EmailIcon style={{ marginLeft: 8, cursor: "pointer", verticalAlign: "middle" }} onClick={() => openEmailLog(item)} />
                    )}
                </span>
            </Flex>
        );
    }

    // Synthesized "Customer Authorizations" entry — always the first item shown when
    // the parent provides a timestamp. Lists all four acknowledgments inline so the
    // admin sees exactly what the customer agreed to before paying. Sits OUTSIDE the
    // sort so it stays pinned to the top regardless of how the rest of the log sorts.
    const authorizationsEntry = authorizationsTimestamp ? (
        <InnerFlex key="customer-authorizations" style={{ alignItems: "flex-start", background: "#F5F7FA", borderRadius: 6, padding: "10px 12px" }}>
            <Flex vertical gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Flex align="center" gap={10}>
                    <ActivityIcon><ClaimIcon /></ActivityIcon>
                    <span style={{ fontWeight: 600 }}>
                        Customer Authorizations
                        {!authorizationsHasExplicitRecord && (
                            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400, marginLeft: 8 }}>
                                (timestamp approximated from claim creation — legacy claim)
                            </span>
                        )}
                    </span>
                </Flex>
                <ul style={{ margin: "4px 0 0 36px", padding: 0, fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
                    <li>I understand I may file directly with the airport or airline for free.</li>
                    <li>I understand I am purchasing a private concierge service.</li>
                    <li>I understand Lost and Found Concierge is not affiliated with any airport, airline, or government agency.</li>
                    <li>I wish to continue with Lost and Found Concierge.</li>
                </ul>
            </Flex>
            <Timestamp>{convertUtcToLocalAndRelativeTime(authorizationsTimestamp, "MM/DD/YYYY HH:mm:ss")}</Timestamp>
        </InnerFlex>
    ) : null;

    return (
        <>
            {status === RequestResults.pending ? (
                <Skeleton paragraph={{ rows: 6 }} active />
            ) : (<>
                {authorizationsEntry}
                {logs?.length > 0 && logs?.map((item) => {
                    return (
                        <InnerFlex key={item.id}>
                            {renderActivityItem(item)}
                            <Flex align="center" gap={10}>
                                {(Number(item?.type) === 8 || Number(item?.type) === LostItemActivityLogType.Note) && (<EditIcon onClick={() => handleEditNoteModalOpen?.("update", item)} />)}
                                <Timestamp>{convertUtcToLocalAndRelativeTime(item?.createdAt, "MM/DD/YYYY HH:mm:ss")}</Timestamp>
                            </Flex>
                        </InnerFlex>
                    )
                })}
            </>)}


            {selectedEmailLogs?.emailLog?.id && <StyledModal
                draggable={true}
                title={"Email Content"}
                width={800}
                centered
                open={Boolean(selectedEmailLogs?.emailLog?.id)}
                onCancel={handleCloseEmailLogsPopup}
                footer={
                    <FooterButtons>
                        <AddButton onClick={handleCloseEmailLogsPopup}>Close</AddButton>
                    </FooterButtons>
                }
            >
                <StyledTitle level={5} style={{ marginTop: "10px" }}><b>Subject : </b> {selectedEmailLogs?.emailLog?.subject}</StyledTitle>
                <StyledTitle level={5}><b>From :</b> {selectedEmailLogs?.emailLog?.from}</StyledTitle>
                <StyledTitle level={5}><b>To : </b>  {selectedEmailLogs?.emailLog?.to}</StyledTitle>
                <StyledTitle level={5}><b>Body :</b></StyledTitle>
                <div
                    style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                        padding: "12px",
                        backgroundColor: "#fafafa",
                        maxHeight: "250px",
                        overflowY: "auto",
                    }}
                >
                    {emailBodyLoading && !selectedEmailLogs?.emailLog?.body
                        ? <Skeleton paragraph={{ rows: 4 }} active />
                        : <div dangerouslySetInnerHTML={{ __html: sanitizeHtml((selectedEmailLogs?.emailLog?.body || "").replace(/\n/g, "<br/>")) }} />}
                </div>
            </StyledModal>}
        </>
    );
};

export default ActivityLogs;
